import React, {useEffect, useRef, useState} from 'react';
import mapboxgl from 'mapbox-gl';
import './map-view.scss';
import turf from 'turf';
import {useNavigate, useSearchParams} from "react-router-dom";
import Infobox from "../../../components/infobox";
import {MapModeControl} from "../../../components/map-mode-control";
import {MapTimelineControl} from "../../../components/map-timeline-control";
import {InfoboxSingleObservation} from "../../../components/infobox-single-observation";
import {InfoboxSpeciesLocationDetail} from "../../../components/infobox-species-location-detail";
import {InfoboxLocationDetail} from "../../../components/infobox-location-detail";
import {getFilteredObservations} from "ebird-mydata-reader";
import {formatDate} from "../../../helpers/helpers";
import {BASE_MONTHS} from "../../../helpers/months";
import {
    ACTIVE_MARKER_LAYER_ID,
    ANIMATION_DELAY,
    DEFAULT_MAP_CENTER,
    EXTRA_SHORT_DELAY,
    MAP_MAX_ZOOM,
    MAPBOX_ACCESS_TOKEN,
    MARKER_LAYER_ID,
    MARKER_SOURCE_ID,
    NEW_LOCATION_SPEED,
    PRE_EBIRD_LIST_BUILDING_MAP_PERIOD_LABEL,
    SAME_LOCATION_SPEED,
    SHORT_DELAY,
    SMALL_SCREEN_CENTER_OFFSET,
    SMALL_SCREEN_QUERY,
    SMALL_SCREEN_ZOOM_REDUCTION,
    STARTING_ZOOM,
    ZOOM_INCREMENT,
    ZOOMED_OUT_THRESHOLD,
    MapMode
} from "../constants";
import type {MapModeType, MapViewProps} from "../types";
import {PRE_EBIRD_LIST_BUILDING_YEAR} from "../../../helpers/storedBirdData";

export default function MapView({
    mode,
    tourMode = false,
    markers,
    year,
    month,
    date,
    homeLocation,
    showPlayControls,
    lifersOnly = false,
    onSetLifersOnly,
    activeSpecies,
    activeLocation,
    setActiveLocation,
    observationsByLocation,
    speciesOptions,
    familyByTaxonomicOrder,
    setObservationImages,
    onShowTimePeriodSelector
}: MapViewProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const mapRoot = useRef<any>(null);
    const map = useRef<any>(null);
    const miniMapRoot = useRef<any>(null);
    const miniMap = useRef<any>(null);
    const modeRef = useRef<MapModeType>(mode);
    const markersRef = useRef(markers);
    const activeSpeciesRef = useRef(activeSpecies);
    const isTourModeRef = useRef(false);
    const pausedRef = useRef(false);

    let lastMarker: any = useRef(null);
    let currentMarker: any = useRef(null);
    let currentIndex: any = useRef(-1);

    const [currentMarkerState, setCurrentMarkerState]: any = useState();
    const [currentIndexState, setCurrentIndexState]: any = useState();
    const [totalMarkers, setTotalMarkers]: any = useState();
    const [paused, setPaused] = useState(false);
    const [lastZoom, setLastZoom] = useState(STARTING_ZOOM);
    const [activeLocationObservations, setActiveLocationObservations] = useState<any[]>();
    const [isMapReady, setIsMapReady] = useState(false);

    let mapTimeout: any = useRef(null);
    const isSpecificSpeciesMode = mode === MapMode.Species && Boolean(activeSpecies);
    const isMapExplorerMode = mode === MapMode.Year || (mode === MapMode.Species && !activeSpecies);
    const shouldShowMapModePanel = isMapExplorerMode || isSpecificSpeciesMode;
    const isTourMode = mode === MapMode.Year && tourMode;
    const shouldFilterLifersOnly = mode === MapMode.Year && !tourMode && lifersOnly;
    const shouldShowYearObservation = isMapExplorerMode && !activeLocation && currentMarkerState;
    const shouldShowInfobox = shouldShowYearObservation || Boolean(activeLocation);
    const shouldClearMarkers = mode === MapMode.Species && !activeSpecies;
    const parsedMonth = month && month !== 'null' ? parseInt(month as string) : undefined;
    const selectedMonthLabel = parsedMonth ? BASE_MONTHS.find(baseMonth => baseMonth.value === parsedMonth)?.label : undefined;
    const periodLabel = date ? formatDate(date) : Number(year) === PRE_EBIRD_LIST_BUILDING_YEAR
        ? PRE_EBIRD_LIST_BUILDING_MAP_PERIOD_LABEL
        : year === 'life' ? 'Your Life List' : [selectedMonthLabel, year].filter(Boolean).join(' ') || 'Life List';
    const setPausedState = (value: boolean) => {
        pausedRef.current = value;
        setPaused(value);
    }

    const selectCurrentMarker = (marker: any, markerIndex: number) => {
        currentIndex.current = markerIndex;
        currentMarker.current = marker;
        setCurrentIndexState(markerIndex);
        setCurrentMarkerState(marker);
        updateMarkerStates();
    }

    const getMarkerKey = (marker: any) => {
        const props = marker?.properties ?? {};
        return [
            props.locationId,
            props.taxonomicOrder,
            props.date,
            props.time,
            props.species
        ].join('|');
    }

    const getCurrentHotspotMarkers = () => {
        const locationId = currentMarker.current?.properties?.locationId;
        if (!locationId) return [];

        return markersRef.current?.features?.filter((feature: any) => feature.properties.locationId === locationId) ?? [];
    }

    const getCurrentHotspotMarkerIndex = (hotspotMarkers: any[]) => {
        const currentKey = getMarkerKey(currentMarker.current);
        return Math.max(0, hotspotMarkers.findIndex(marker => getMarkerKey(marker) === currentKey));
    }

    const selectHotspotMarker = (marker: any) => {
        const currentMarkers = markersRef.current;
        const markerIndex = currentMarkers?.features?.findIndex((feature: any) => getMarkerKey(feature) === getMarkerKey(marker)) ?? -1;
        selectCurrentMarker(marker, markerIndex >= 0 ? markerIndex : currentIndex.current);
    }

    const navigateHotspotMarker = (direction: -1 | 1) => {
        const hotspotMarkers = getCurrentHotspotMarkers();
        if (hotspotMarkers.length < 2) return;

        const currentHotspotMarkerIndex = getCurrentHotspotMarkerIndex(hotspotMarkers);
        const nextHotspotMarkerIndex = (currentHotspotMarkerIndex + direction + hotspotMarkers.length) % hotspotMarkers.length;
        selectHotspotMarker(hotspotMarkers[nextHotspotMarkerIndex]);
    }

    const setMapExplorerMode = (nextMode: MapModeType, nextTourMode = false) => {
        const nextParams = new URLSearchParams(searchParams);

        nextParams.set('mode', nextMode);
        nextParams.delete('species');

        if (nextTourMode) {
            nextParams.set('tour', 'true');
            nextParams.delete('lifersOnly');
        } else {
            nextParams.delete('tour');
        }

        if (nextMode !== MapMode.Year) {
            nextParams.delete('lifersOnly');
        }

        navigate(`/map?${nextParams.toString()}`);
    }

    const selectSpeciesOption = (speciesCode: string) => {
        const nextParams = new URLSearchParams(searchParams);

        nextParams.set('mode', MapMode.Species);
        nextParams.set('species', speciesCode);
        nextParams.delete('tour');
        nextParams.delete('lifersOnly');

        navigate(`/map?${nextParams.toString()}`);
    }

    const closeCurrentObservationCard = () => {
        currentIndex.current = -1;
        currentMarker.current = null;
        setCurrentIndexState(-1);
        setCurrentMarkerState(null);
        updateMarkerStates();
    }

    const getInitialMapCenter = (): [number, number] => {
        if (
            homeLocation &&
            Number.isFinite(homeLocation.latitude) &&
            Number.isFinite(homeLocation.longitude)
        ) {
            return [homeLocation.longitude, homeLocation.latitude];
        }

        return DEFAULT_MAP_CENTER;
    }

    useEffect(() => {
        modeRef.current = mode;
        updateMarkerLayerStyles();
        // Marker styling is an imperative Mapbox side effect keyed to mode only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        markersRef.current = markers;
    }, [markers]);

    useEffect(() => {
        activeSpeciesRef.current = activeSpecies;
        updateMarkerLayerStyles();
        // Marker styling is an imperative Mapbox side effect keyed to selected species state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSpecies]);

    useEffect(() => {
        isTourModeRef.current = isTourMode;
        updateMarkerLayerStyles();
        // Marker styling is an imperative Mapbox side effect keyed to tour/explorer mode.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTourMode]);

    useEffect(() => {
        if (map.current) { return; }

        const initialMapCenter = getInitialMapCenter();

        map.current = new mapboxgl.Map({
            container: mapRoot.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            /*projection: {
              name: 'globe'
            },*/
            center: initialMapCenter,
            pitch: 25,
            zoom: 4,
            maxZoom: getMapMaxZoom(),
            keyboard: false,
            attributionControl: false
        });

        updateZoomProps();
        map.current.on('zoom', updateZoomProps);
        window.addEventListener('resize', updateResponsiveMapSettings);

        miniMap.current = new mapboxgl.Map({
            container: miniMapRoot.current,
            style: 'mapbox://styles/mapbox/light-v10',
            center: initialMapCenter,
            zoom: 1,
            interactive: false,
            attributionControl: false
        });

        initMiniMapFollow();
        map.current.once('load', () => {
            setupCanvasMarkerLayers();
            setIsMapReady(true);
        });

        document.body.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('resize', updateResponsiveMapSettings);
            document.body.removeEventListener('keydown', handleKeyPress);
            stop();
            map.current?.remove();
            miniMap.current?.remove();
            map.current = null;
            miniMap.current = null;
        }
        // Mapbox setup is intentionally mounted once; the handlers read current refs/state during map interactions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSmallScreenMap = () => {
        return window.matchMedia(SMALL_SCREEN_QUERY).matches;
    }

    const getMapMaxZoom = () => {
        const modeMaxZoom = [MapMode.Year, MapMode.Species].includes(modeRef.current)
            ? MAP_MAX_ZOOM + 1
            : MAP_MAX_ZOOM;
        return isSmallScreenMap() ? modeMaxZoom - SMALL_SCREEN_ZOOM_REDUCTION : modeMaxZoom;
    }

    const getPlaybackZoom = (zoomLevel: number) => {
        return isSmallScreenMap() ? Math.max(zoomLevel - SMALL_SCREEN_ZOOM_REDUCTION, 1) : zoomLevel;
    }

    const getPlaybackOffset = (): [number, number] => {
        return isSmallScreenMap() ? SMALL_SCREEN_CENTER_OFFSET : [0, 0];
    }

    const updateResponsiveMapSettings = () => {
        map.current?.setMaxZoom(getMapMaxZoom());
    }

    const handleKeyPress = (e: KeyboardEvent) => {
        // console.log(e);
        if (modeRef.current === MapMode.Year && isTourModeRef.current) {
            switch (e.code) {
                case 'ArrowLeft':
                    e.shiftKey ? toStart() : rewind();
                    break;
                case 'ArrowRight':
                    e.shiftKey ? toEnd() : fastForward();
                    break;
                case 'Space':
                    togglePaused();
                    break;
            }
        }
    }

    const updateZoomProps = () => {
        if (!mapRoot.current || !map.current || !miniMapRoot.current) return;

        mapRoot.current.className = `map-root zoom-${parseInt(map.current.getZoom())}`

        if (map.current.getZoom() < ZOOMED_OUT_THRESHOLD) {
            miniMapRoot.current.className = 'minimap-root state-hidden';
        } else {
            miniMapRoot.current.className = 'minimap-root';
        }
    }

    const initMiniMapFollow = () => {
        updateMiniMap();
        map.current.on('move', updateMiniMap);
    }

    const updateMiniMap = () => {
        miniMap.current.jumpTo({
            center: map.current.getCenter(),
            zoom: 4.5,
            speed: 0.5
        });
    }

    const handleMarkerClick = (locationId: string) => {
        const currentMarkers = markersRef.current;
        const foundMarkerIndex = currentMarkers?.features?.findIndex((el: any) => el.properties.locationId === locationId) ?? -1;
        const foundMarker = foundMarkerIndex >= 0 ? currentMarkers.features[foundMarkerIndex] : undefined;

        const isMarkerExplorerMode = modeRef.current === MapMode.Year
            || (modeRef.current === MapMode.Species && !activeSpeciesRef.current);

        if (isMarkerExplorerMode) {
            if (!isTourModeRef.current && foundMarker) {
                stop();
                setPausedState(false);
                setActiveLocation(undefined);
                selectCurrentMarker(foundMarker, foundMarkerIndex);
            }

            return;
        }

        setActiveLocation(locationId);

        if (foundMarker) {
            selectCurrentMarker(foundMarker, foundMarkerIndex);
        }
    }

    useEffect(() => {
        if (!isMapReady) return;
        // console.log(markers);
        setTotalMarkers(markers?.features?.length ?? 0);

        stop();
        reset();

        if (!shouldClearMarkers && markers?.features?.length) {
            addAllMarkers();
            if (isTourMode) {
                playNext();
            } else {
                done();
            }
        } else if (shouldClearMarkers) {
            clearCanvasMarkers();
        }
        // Marker playback is managed through refs and Mapbox side effects; rerunning for every handler identity breaks playback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMapReady, markers, mode, isTourMode, activeSpecies]);

    useEffect(() => {
        setActiveLocation(undefined);
    }, [mode, setActiveLocation]);

    useEffect(() => {
        if (activeLocation) {
            if (mode === MapMode.Species && activeSpecies) {
                const filteredFeatures = markers.features.filter((el: any) => el.properties.locationId === activeLocation);
                setActiveLocationObservations(filteredFeatures);
            } else if (activeLocation) {
                setPausedState(true);
                stop();

                const foundLocation = observationsByLocation.find(el => el.locationId === activeLocation);
                if (foundLocation) {
                    let filteredObservations = date
                        ? foundLocation.observations.filter(observation => observation.date === date)
                        : getFilteredObservations(foundLocation.observations, parseInt(year as string), parseInt(month as string), true);
                    if (shouldFilterLifersOnly) {
                        filteredObservations = filteredObservations.filter(observation => observation.isLifer);
                    }
                    filteredObservations = setObservationImages(filteredObservations);
                    setActiveLocationObservations(filteredObservations);
                }
            }
        }
    }, [activeLocation, activeSpecies, date, markers.features, mode, month, observationsByLocation, setObservationImages, shouldFilterLifersOnly, year]);

    const reset = () => {
        currentIndex.current = -1;
        setCurrentIndexState(-1);
        setPausedState(false);

        currentMarker.current = null;
        setCurrentMarkerState(null);

        lastMarker.current = null;

        clearCanvasMarkers();

        setActiveLocation(undefined);

    }

    const stop = () => {
        map.current?.stop();
        miniMap.current?.stop();
        clearTimeout(mapTimeout.current);
    }

    const playNext = () => {
        const currentMarkers = markersRef.current;

        currentIndex.current = currentIndex.current + 1;
        currentMarker.current = currentMarkers?.features?.[currentIndex.current];
        if (!currentMarker.current) return;

        setActiveLocation(undefined);
        setCurrentIndexState(currentIndex.current);
        setCurrentMarkerState(currentMarker.current);

        updateMarkerStates();

        let zoomLevel, speed;
        if (lastMarker.current?.properties.locationName === currentMarker.current?.properties.locationName) {
            const zl = lastZoom + ZOOM_INCREMENT;
            setLastZoom(zl);
            zoomLevel = getPlaybackZoom(zl);
            speed = SAME_LOCATION_SPEED;
        } else {
            const zl = STARTING_ZOOM;
            setLastZoom(zl);
            zoomLevel = getPlaybackZoom(zl);
            speed = NEW_LOCATION_SPEED;
        }

        map.current.flyTo({
            center: currentMarker.current.geometry.coordinates,
            zoom: zoomLevel,
            offset: getPlaybackOffset(),
            curve: 2,
            speed: speed
        });

        map.current.once('moveend', () => {
            // Duration the slide is on screen after interaction
            let delay = !movedSinceLastMarker() ? SHORT_DELAY : ANIMATION_DELAY;

            if (currentIndex.current >= currentMarkers.features.length - 1) {
                mapTimeout.current = setTimeout( () => {
                    done();
                }, delay);
            } else {
                clearTimeout(mapTimeout.current);
                mapTimeout.current = setTimeout( () => {
                    // Increment index
                    lastMarker.current =currentMarker.current;
                    playNext();
                }, delay);
            }

        });
    }

    const movedSinceLastMarker = () => {
        return (lastMarker.current?.properties?.locationName !== currentMarker.current?.properties?.locationName);
    }

    const togglePaused = () => {
        const wasPaused = pausedRef.current;
        setPausedState(!wasPaused);
        if (wasPaused) {
            clearTimeout(mapTimeout.current);
            mapTimeout.current = setTimeout(() => {
                playNext();
            }, EXTRA_SHORT_DELAY);
        } else {
            stop();
        }
    }

    const toStart = () => {
        currentIndex.current = -1;
        stop();
        setPausedState(false);
        playNext();
    }

    const rewind = () => {
        currentIndex.current = Math.max( currentIndex.current - 2, -1);

        stop();
        setPausedState(false);
        playNext();
    }

    const fastForward = () => {
        const currentMarkers = markersRef.current;
        if (currentIndex.current >= currentMarkers?.features?.length - 1) { return; }

        stop();
        setPausedState(false)
        playNext();
    }

    const toEnd = () => {
        const currentMarkers = markersRef.current;
        currentIndex.current = Math.max(currentMarkers?.features?.length - 2, -1);

        stop();
        setPausedState(false);
        playNext();
    }

    const done = () => {
        lastMarker.current = null;
        const currentMarkers = markersRef.current;

        try {
            const bounds = turf.bbox(currentMarkers);
            map.current.fitBounds(bounds, { padding: 150 });
        } catch(e) {
            console.log('Unable to zoom to bounds');
        }
    }

    const addAllMarkers = () => {
        addCanvasMarkers();
    }

    const setupCanvasMarkerLayers = () => {
        if (!map.current || !map.current.isStyleLoaded()) return;

        if (!map.current.getSource(MARKER_SOURCE_ID)) {
            map.current.addSource(MARKER_SOURCE_ID, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });
        }

        if (!map.current.getLayer(MARKER_LAYER_ID)) {
            map.current.addLayer({
                id: MARKER_LAYER_ID,
                type: 'circle',
                source: MARKER_SOURCE_ID,
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        3, 4,
                        8, 7,
                        14, 14
                    ],
                    'circle-color': '#51a488',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.9
                }
            });

            map.current.on('click', MARKER_LAYER_ID, (event: any) => {
                const locationId = event.features?.[0]?.properties?.locationId;
                if (locationId) handleMarkerClick(locationId);
            });

            map.current.on('mouseenter', MARKER_LAYER_ID, () => {
                if (modeRef.current !== MapMode.Year || !isTourModeRef.current) {
                    map.current.getCanvas().style.cursor = 'pointer';
                }
            });

            map.current.on('mouseleave', MARKER_LAYER_ID, () => {
                map.current.getCanvas().style.cursor = '';
            });
        }

        if (!map.current.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.addLayer({
                id: ACTIVE_MARKER_LAYER_ID,
                type: 'circle',
                source: MARKER_SOURCE_ID,
                filter: ['==', ['get', 'locationId'], ''],
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        3, 6,
                        8, 10,
                        14, 15
                    ],
                    'circle-color': '#c03221',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.92
                }
            });
        }

        updateMarkerLayerStyles();
    }

    const getDefaultMarkerRadius = () => {
        if (
            (modeRef.current === MapMode.Year && !isTourModeRef.current)
            || modeRef.current === MapMode.Species
        ) {
            return [
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 11,
                7, 9,
                11, 7.5,
                14, 5
            ];
        }

        return [
            'interpolate',
            ['linear'],
            ['zoom'],
            3, 4,
            8, 7,
            14, 14
        ];
    }

    const updateMarkerLayerStyles = () => {
        if (!map.current?.getLayer(MARKER_LAYER_ID)) return;

        const isYearExplorerMarker = (modeRef.current === MapMode.Year && !isTourModeRef.current)
            || (modeRef.current === MapMode.Species && !activeSpeciesRef.current);

        map.current.setPaintProperty(MARKER_LAYER_ID, 'circle-radius', getDefaultMarkerRadius());
        map.current.setPaintProperty(
            MARKER_LAYER_ID,
            'circle-color',
            modeRef.current === MapMode.Species || isYearExplorerMarker ? '#c03221' : '#51a488'
        );
        map.current.setPaintProperty(
            MARKER_LAYER_ID,
            'circle-opacity',
            modeRef.current === MapMode.Species || isYearExplorerMarker ? 0.94 : 0.9
        );
    }

    const addCanvasMarkers = () => {
        if (!map.current || !isMapReady) {
            return;
        }

        setupCanvasMarkerLayers();
        const markerSource = map.current.getSource(MARKER_SOURCE_ID) as mapboxgl.GeoJSONSource|undefined;
        markerSource?.setData(markersRef.current);
        updateMarkerStates();
    }

    const clearCanvasMarkers = () => {
        if (!map.current?.getSource(MARKER_SOURCE_ID)) return;

        const markerSource = map.current.getSource(MARKER_SOURCE_ID) as mapboxgl.GeoJSONSource;
        markerSource.setData({
            type: 'FeatureCollection',
            features: []
        });

        if (map.current.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.setFilter(ACTIVE_MARKER_LAYER_ID, ['==', ['get', 'locationId'], '']);
        }
    }

    const updateMarkerStates = () => {
        if (map.current?.getLayer(ACTIVE_MARKER_LAYER_ID)) {
            map.current.setFilter(
                ACTIVE_MARKER_LAYER_ID,
                ['==', ['get', 'locationId'], currentMarker.current?.properties?.locationId ?? '']
            );
        }
    }

    const hotspotMarkers = !isTourMode ? getCurrentHotspotMarkers() : [];
    const hotspotMarkerIndex = getCurrentHotspotMarkerIndex(hotspotMarkers);

    return (
        <>
            <div className="map-root" ref={mapRoot}></div>
            <div className="minimap-root" ref={miniMapRoot}></div>
            {shouldShowMapModePanel ?
                <MapModeControl
                    mode={mode}
                    tourMode={tourMode}
                    activeSpecies={activeSpecies}
                    periodLabel={periodLabel}
                    lifersOnly={lifersOnly}
                    speciesOptions={speciesOptions}
                    onSetLifersOnly={onSetLifersOnly}
                    onSelectSpecies={selectSpeciesOption}
                    onSetMapExplorerMode={setMapExplorerMode}
                    onShowTimePeriodSelector={onShowTimePeriodSelector}
                />
            : null}
            {shouldShowInfobox ?
                <Infobox>
                    {
                        shouldShowYearObservation ?
                            <InfoboxSingleObservation
                                currentMarker={currentMarkerState}
                                currentIndex={currentIndexState}
                                total={totalMarkers}
                                year={year}
                                month={month}
                                familyLink={familyByTaxonomicOrder.get(currentMarkerState?.properties?.taxonomicOrder)}
                                familyByTaxonomicOrder={familyByTaxonomicOrder}
                                hotspotMarkers={hotspotMarkers}
                                hotspotIndex={hotspotMarkerIndex}
                                hotspotTotal={hotspotMarkers.length}
                                onPreviousHotspotMarker={hotspotMarkers.length > 1 ? () => navigateHotspotMarker(-1) : undefined}
                                onNextHotspotMarker={hotspotMarkers.length > 1 ? () => navigateHotspotMarker(1) : undefined}
                                onClose={closeCurrentObservationCard}
                            /> : null
                    }
                    {
                        mode === MapMode.Species && activeSpecies && activeLocation ?
                            <InfoboxSpeciesLocationDetail
                                taxonomicOrder={activeSpecies}
                                observations={activeLocationObservations}
                                familyLink={familyByTaxonomicOrder.get(activeSpecies)}
                                onClose={() => setActiveLocation(undefined)}
                            />
                            : null
                    }
                    {
                        (mode === MapMode.Location || mode === MapMode.Year) && activeLocation ?
                            <InfoboxLocationDetail
                                observations={activeLocationObservations}
                                year={year}
                                month={month}
                            /> : null
                    }
                </Infobox>
            : null}
            {
                showPlayControls && isTourMode ?
                    <MapTimelineControl
                        paused={paused}
                        currentMarker={currentMarkerState}
                        markers={markers}
                        year={year}
                        month={month}
                        togglePaused={togglePaused}
                        rewind={rewind}
                        fastForward={fastForward}
                        toStart={toStart}
                        toEnd={toEnd}
                        onShowTimePeriodSelector={onShowTimePeriodSelector}
                        showPeriodControl={false}
                    />
                : null
            }

        </>
    )
}
